// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: export de la liste des actions — sélection, métadonnées, payload
// ═══════════════════════════════════════════════════════════════════════════
//
// Décisions pures extraites de page-actions/render-page._exportActions
// (juin 2026). Les libellés i18n (rôles cochés, comité, scope, priorité) sont
// résolus côté page (couplés à t()) puis INJECTÉS ici : ce module ne décide que
// la sélection des ids, l'omission des défauts dans les métadonnées, et la
// forme du payload envoyé au backend.

/** Métadonnées de filtres jointes à l'export (forme attendue par le backend). */
export interface ActionsExportFilters {
  programme: string;
  projet: string;
  // `role` n'existe plus côté front (remplacé par all_actions + mon_role) ;
  // conservé vide pour rétro-compat backend.
  role: string;
  mon_role: string;
  scope_mode: 'all' | 'mine';
  scope_label: string;
  hide_done: boolean;
  date_range: string;
  date_range_start: string;
  date_range_end: string;
  all_actions: boolean;
  committee_label: string;
  priority_min_label: string;
}

export interface ActionsExportPayload {
  fmt: string;
  action_ids: Array<number | null | undefined>;
  filters: ActionsExportFilters;
  user_id: number | null;
  export_dt: string;
}

/**
 * Ids à exporter : sans sélection explicite, tous les ids filtrés ; avec une
 * sélection (`override`), l'intersection avec les filtrés — garde-fou contre
 * un id resté coché après un changement de filtre.
 */
export function selectExportActionIds<T>(filteredIds: T[], override?: T[]): T[] {
  if (!override) return filteredIds;
  const present = new Set(filteredIds);
  return override.filter((id) => present.has(id));
}

/** Libellés i18n pré-résolus côté page (vides quand non applicables). */
export interface ExportFilterLabels {
  monRole: string;
  scopeLabel: string;
  committeeLabel: string;
  priorityMinLabel: string;
}

/**
 * Assemble les métadonnées de filtres : encode les décisions d'omission des
 * valeurs par défaut (programme/date « all » → chaîne vide) et le mapping du
 * scope ; les libellés i18n sont fournis par l'appelant.
 */
export function buildExportFilters(
  s: {
    fProgram: string;
    fProject: string;
    fAll: boolean;
    fHideDone: boolean;
    fDateMode: string;
    fDateStart: string;
    fDateEnd: string;
  },
  labels: ExportFilterLabels,
): ActionsExportFilters {
  return {
    programme: s.fProgram === 'all' ? '' : String(s.fProgram),
    projet: s.fProject,
    role: '',
    mon_role: labels.monRole,
    scope_mode: s.fAll ? 'all' : 'mine',
    scope_label: labels.scopeLabel,
    hide_done: s.fHideDone,
    date_range: s.fDateMode === 'all' ? '' : s.fDateMode,
    date_range_start: s.fDateStart || '',
    date_range_end: s.fDateEnd || '',
    all_actions: s.fAll,
    committee_label: labels.committeeLabel,
    priority_min_label: labels.priorityMinLabel,
  };
}

/** Assemble le payload d'export final. */
export function buildExportPayload(input: {
  fmt: string;
  actionIds: Array<number | null | undefined>;
  filters: ActionsExportFilters;
  userId: number | null;
  exportDt: string;
}): ActionsExportPayload {
  return {
    fmt: input.fmt,
    action_ids: input.actionIds,
    filters: input.filters,
    user_id: input.userId,
    export_dt: input.exportDt,
  };
}
